import { LuPlus } from '@react-icons/all-files/lu/LuPlus'
import isEqual from 'lodash/isEqual'
import React, { useEffect, useState } from 'react'

import { useTranslation } from '@disco/i18n/use-translation'
import {
  clientLibraryFeaturedTracksAlter,
  clientLibraryFeaturedTracksLoad,
  clientLibraryFeaturedTracksReorder,
} from '@disco/state-management/actions/client-library-track-actions'
import { getClientLibraryTracks } from '@disco/state-management/selectors/client-libraries'
import { pushUINotification } from '@disco/utils/notifications'

import type { ClientLibraryId } from 'types/client-library'
import type { Track, TrackId } from 'types/track'

import { Button, Flex, Text, Tooltip } from 'components/ds'
import PageContainer from 'components/ui/page-container'

import { useDispatch, useSelector } from 'hooks/use-redux'

import AddTopTracksDialog from './add-top-tracks-dialog'
import { MAX_TOP_TRACKS } from './constants'
import TopTracksContent from './top-tracks-content'

type Props = {
  libraryId: ClientLibraryId
}

/**
 * "Manage top tracks" section of the artist profile page. Top tracks are the
 * catalog's featured tracks, picked from anywhere in the business library.
 * @param libraryId - ID of the artist profile catalog
 */
const TopTracks = ({ libraryId }: Props) => {
  const { t } = useTranslation(['catalogs-management', 'common'])
  const dispatch = useDispatch()
  const featured = useSelector(
    state => getClientLibraryTracks(state, libraryId)?.featured,
  )
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  // The thunks undo a failed change by restoring the list from before it, so only one
  // change can be saving at a time
  const [isSaving, setIsSaving] = useState(false)
  const topTracks: Track[] = featured?.list ?? []
  const topTrackIds = topTracks.map(track => track.id)
  const remaining = Math.max(MAX_TOP_TRACKS - topTracks.length, 0)

  useEffect(() => {
    dispatch(clientLibraryFeaturedTracksLoad(libraryId, ''))
  }, [dispatch, libraryId])

  const loadTopTracks = () => {
    dispatch(clientLibraryFeaturedTracksLoad(libraryId, ''))
  }

  const save = async (change: Promise<unknown>, errorMessage: string) => {
    setIsSaving(true)
    try {
      await change
      pushUINotification({
        message: t('catalogs-management:topTracksUpdated', 'Tracks updated!'),
        level: 'success',
      })
    } catch {
      pushUINotification({ message: errorMessage, level: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  const removeTrack = (trackId: TrackId) => {
    save(
      dispatch(clientLibraryFeaturedTracksAlter(libraryId, [], [trackId])),
      t(
        'catalogs-management:topTracksCouldNotBeUpdated',
        'Tracks could not be updated, please try again.',
      ),
    )
  }

  const reorder = ({ order }: { order: string[] }) => {
    const trackIds = order.map(Number)

    if (!isEqual(topTrackIds, trackIds)) {
      save(
        dispatch(clientLibraryFeaturedTracksReorder(libraryId, trackIds)),
        t(
          'catalogs-management:topTracksCouldNotBeReordered',
          'Tracks could not be reordered, please try again.',
        ),
      )
    }
  }

  const openDialog = () => {
    setIsDialogOpen(true)
  }

  const addButton = (
    <Button disabled={remaining === 0 || isSaving} onClick={openDialog}>
      <LuPlus />
      {t('catalogs-management:addTrack', 'Add track')}
    </Button>
  )

  return (
    <PageContainer.Section
      title={t('catalogs-management:manageTopTracks', 'Manage top tracks')}
      action={
        featured?.loaded && (
          <Flex align="center" gap="3">
            <Text size="2" color="gray">
              {topTracks.length}/{MAX_TOP_TRACKS}
            </Text>
            {remaining === 0 ? (
              <Tooltip
                content={t(
                  'catalogs-management:topTracksLimitReached',
                  'You can add up to {{max}} top tracks',
                  {
                    max: MAX_TOP_TRACKS,
                  },
                )}
              >
                <span>{addButton}</span>
              </Tooltip>
            ) : (
              addButton
            )}
          </Flex>
        )
      }
    >
      <TopTracksContent
        libraryId={libraryId}
        tracks={topTracks}
        isLoaded={Boolean(featured?.loaded)}
        hasError={Boolean(featured?.error)}
        isSaving={isSaving}
        onRetry={loadTopTracks}
        onAdd={openDialog}
        onRemove={removeTrack}
        onReorder={reorder}
      />
      <AddTopTracksDialog
        libraryId={libraryId}
        excludeIds={topTrackIds}
        remaining={remaining}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />
    </PageContainer.Section>
  )
}

export default TopTracks
